/**
 * /customer/help — Help & Support (Phase 2d).
 *
 * Static client-side FAQ + contact channels — mirrors mobile
 * (customer)/help.tsx (lines 23–40 FAQ array, 91–130 contact card)
 * verbatim. Operator §8 = STATIC; no FAQ API exists.
 *
 * The WhatsApp slot is intentionally an inert placeholder (operator §9).
 */
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, HelpCircle, Mail, MessageCircle, ChevronRight } from 'lucide-react';

// FAQ + Contact arrays are mirrored verbatim from
// /app/mobile/app/(customer)/help.tsx — keep these in sync if mobile
// ever changes its copy.
const FAQS = [
  {
    q: 'How do I collect stamps?',
    a: "Visit any participating store, make a purchase that meets the brand\u2019s minimum, then show your Stamp QR code to the cashier. They\u2019ll scan it and your stamps update automatically.",
  },
  {
    q: 'How do I redeem rewards?',
    a: 'When you have enough stamps for a reward, open the Rewards tab, select the reward, and present the redemption code to the cashier at the store.',
  },
  {
    q: 'My reward code does not work.',
    a: 'Reward codes expire after a few minutes for security. Open the Rewards tab and generate a fresh code, then present it immediately to the cashier.',
  },
  {
    q: 'Why did I not receive stamps?',
    a: "Stamps are awarded based on the brand\u2019s minimum spend rule. If your purchase was below the threshold, no stamps are earned. You can also check your Activity History to see your latest transactions.",
  },
];

export default function CustomerHelpPage() {
  const navigate = useNavigate();
  return (
    <div className="space-y-6" data-testid="customer-help-screen">
      {/* Header */}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate(-1)}
          aria-label="Back"
          data-testid="customer-help-back"
          className="w-9 h-9 rounded-full bg-[#15151B] border border-white/10 grid place-items-center text-white/75 hover:text-gold hover:border-gold/35 transition-colors"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] font-mono text-gold">
            Support
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold leading-tight">
            Help &amp; Support
          </h1>
        </div>
      </div>

      {/* FAQ section */}
      <section
        className="rounded-3xl border border-gold/25 bg-[#15151B] px-5 md:px-6 py-5"
        data-testid="customer-help-faq"
      >
        <div className="text-base font-semibold mb-4">
          Frequently Asked Questions
        </div>
        <div className="divide-y divide-white/8">
          {FAQS.map((faq, i) => (
            <div
              key={faq.q}
              className={i === 0 ? 'pb-4' : i === FAQS.length - 1 ? 'pt-4' : 'py-4'}
              data-testid={`customer-help-faq-${i}`}
            >
              <div className="flex items-start gap-2.5">
                <HelpCircle size={18} className="text-gold mt-0.5 shrink-0" />
                <div className="text-sm font-semibold text-white/90 leading-snug">
                  {faq.q}
                </div>
              </div>
              <div className="text-sm text-white/65 leading-relaxed mt-2 ml-7">
                {faq.a}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Contact section */}
      <section
        className="rounded-3xl border border-gold/25 bg-[#15151B] px-5 md:px-6 py-5"
        data-testid="customer-help-contact"
      >
        <div className="text-base font-semibold mb-4">Contact Us</div>
        <a
          href="mailto:support@onestamp.id"
          data-testid="customer-help-contact-email"
          className="flex items-center gap-3 py-2 group"
        >
          <div className="w-9 h-9 rounded-full bg-gold/15 border border-gold/30 grid place-items-center text-gold shrink-0">
            <Mail size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] font-mono text-white/45">
              Email
            </div>
            <div className="text-sm font-medium text-white/90 mt-0.5 group-hover:text-gold transition-colors truncate">
              support@onestamp.id
            </div>
          </div>
          <ChevronRight size={16} className="text-white/45 shrink-0" />
        </a>
        <div className="h-px bg-white/8 my-2" />
        <div
          className="flex items-center gap-3 py-2"
          data-testid="customer-help-contact-whatsapp"
        >
          <div className="w-9 h-9 rounded-full bg-white/5 border border-white/10 grid place-items-center text-white/45 shrink-0">
            <MessageCircle size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.16em] font-mono text-white/45">
              WhatsApp
            </div>
            <div className="text-sm italic text-white/45 mt-0.5">
              WhatsApp support coming soon
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
